import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { customerFetchPartnerBtwApplicationById } from "../../core/axios/endpoints";
import BtwStatusPill from "../../components/btw-status-pill";
import { batteryUsageLabel, genderLabel } from "../../core/btw-field-labels";

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString("nl-NL") : "-");
const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";
const fmtEUR = (n) =>
  `€ ${Number(n ?? 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const INVOICE_STATUS_LABELS = {
  pending: { label: "Nog te factureren", color: "bg-slate-100 text-slate-700" },
  invoiced: { label: "Gefactureerd", color: "bg-sky-100 text-sky-800" },
  paid: { label: "Factuur betaald", color: "bg-emerald-100 text-emerald-800" },
  credited: { label: "Gecrediteerd", color: "bg-rose-100 text-rose-800" },
};

const Pill = ({ map, value }) => {
  const cfg = map[value] ?? { label: value || "-", color: "bg-slate-100 text-slate-700" };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.color}`}
    >
      {cfg.label}
    </span>
  );
};

const Section = ({ title, children }) => (
  <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200">
    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-gray-500">
      {title}
    </h3>
    <dl className="space-y-3">{children}</dl>
  </div>
);

const Row = ({ label, value, mono = false, masked = false }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
    <dt className="text-gray-500">{label}</dt>
    <dd
      className={`text-right text-gray-900 ${mono ? "font-mono text-xs" : ""} ${
        masked ? "text-gray-400" : ""
      }`}
    >
      {value || "-"}
    </dd>
  </div>
);

const PartnerBtwTeruggaveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    customerFetchPartnerBtwApplicationById(id)
      .then((res) => {
        if (cancelled) return;
        setData(res?.data ?? res);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(
          e?.response?.status === 404
            ? "Deze aanvraag is niet gevonden of u heeft er geen toegang toe."
            : e?.message ?? "Kon aanvraag niet laden.",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content p-8 text-center text-gray-500">Laden…</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="page-wrapper">
        <div className="content p-8 text-center">
          <p className="text-rose-700">{error}</p>
          <Link
            to="/panel/customer/btw-teruggaves"
            className="mt-4 inline-block rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Terug naar overzicht
          </Link>
        </div>
      </div>
    );
  }

  const step1 = data.step1 ?? {};
  const step3 = data.step3 ?? {};
  const fullName = `${step1.voorletters ?? ""} ${step1.tussenvoegsel ?? ""} ${step1.achternaam ?? ""}`
    .replace(/\s+/g, " ")
    .trim();

  return (
    <div className="page-wrapper">
      <div className="content p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <button
              type="button"
              onClick={() => navigate("/panel/customer/btw-teruggaves")}
              className="mb-2 inline-flex items-center gap-1 text-xs uppercase tracking-wider text-gray-500 hover:text-gray-900"
            >
              <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M9.7 4.3a1 1 0 010 1.4L6.4 9H17a1 1 0 110 2H6.4l3.3 3.3a1 1 0 11-1.4 1.4l-5-5a1 1 0 010-1.4l5-5a1 1 0 011.4 0z"
                  clipRule="evenodd"
                />
              </svg>
              Terug naar overzicht
            </button>
            <h1 className="font-display text-2xl text-gray-900">{fullName}</h1>
            <p className="text-sm text-gray-500">
              Aanvraag ingediend op {fmtDate(data.createdAt)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <BtwStatusPill status={data.workflowStatusId} />
            <Pill
              map={INVOICE_STATUS_LABELS}
              value={data.partnerInvoice?.status ?? "pending"}
            />
          </div>
        </div>

        {/* PII-info banner */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          <strong>Privacy:</strong> BSN, IBAN en geboortedatum worden ter
          bescherming van de klant gemaskeerd. De volledige gegevens zijn alleen
          inzichtelijk voor Subsidie2gether.
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Klant */}
          <Section title="Klantgegevens">
            <Row label="Naam" value={fullName} />
            <Row label="Geslacht" value={genderLabel(step1.geslacht) || "-"} />
            <Row label="Email" value={step1.email} />
            <Row label="Telefoon" value={step1.telefoon} />
            <Row
              label="Adres"
              value={
                [step1.straatnaam, step1.huisnummer]
                  .filter(Boolean)
                  .join(" ") || "-"
              }
            />
            <Row
              label="Postcode + plaats"
              value={[step1.postcode, step1.plaatsnaam].filter(Boolean).join(", ")}
            />
          </Section>

          {/* Aanvraag-details */}
          <Section title="Aanvraag-details">
            <Row label="Geboortedatum" value={step3.geboortedatum} masked />
            <Row label="BSN" value={step3.bsn} masked mono />
            <Row label="IBAN" value={step3.iban} masked mono />
            <Row label="Tenaamstelling IBAN" value={step3.ibanHolder} />
            <Row label="Installatiedatum" value={fmtDate(step3.installationDate)} />
            <Row label="Factuurdatum" value={fmtDate(step3.invoiceDate)} />
            <Row
              label="Batterij capaciteit"
              value={
                step3.batteryCapacityKwh
                  ? `${step3.batteryCapacityKwh} kWh`
                  : "-"
              }
            />
            <Row label="Gebruik" value={batteryUsageLabel(step3.batteryUsage)} />
            <Row
              label="Dynamisch energiecontract"
              value={step3.dynamicEnergyContract}
            />
            <Row
              label="Aantal documenten"
              value={`${data.documentsCount ?? 0} bestand(en)`}
            />
          </Section>
        </div>

        {/* Factuur-info (alleen als gefactureerd EN populated, niet alleen ObjectId) */}
        {data.partnerInvoice?.invoiceId &&
        typeof data.partnerInvoice.invoiceId === "object" ? (
          <Section title="Factuur naar u">
            <Row
              label="Factuurnummer"
              value={data.partnerInvoice.invoiceId.invoiceNumber ?? "-"}
              mono
            />
            <Row
              label="Status"
              value={
                <Pill
                  map={INVOICE_STATUS_LABELS}
                  value={data.partnerInvoice.status ?? "pending"}
                />
              }
            />
            <Row
              label="Bedrag (incl. btw)"
              value={
                typeof data.partnerInvoice.invoiceId.totalIncl === "number"
                  ? fmtEUR(data.partnerInvoice.invoiceId.totalIncl)
                  : "-"
              }
            />
            <Row
              label="Factuurdatum"
              value={
                data.partnerInvoice.invoiceId.invoiceDate
                  ? fmtDate(data.partnerInvoice.invoiceId.invoiceDate)
                  : "-"
              }
            />
            {data.partnerInvoice.invoicedAt ? (
              <Row
                label="Gefactureerd op"
                value={fmtDate(data.partnerInvoice.invoicedAt)}
              />
            ) : null}
            {data.partnerInvoice.paidAt ? (
              <Row label="Betaald op" value={fmtDate(data.partnerInvoice.paidAt)} />
            ) : null}
          </Section>
        ) : null}

        {/* Voortgang (alleen partner-relevante events) */}
        {Array.isArray(data.activities) && data.activities.length > 0 ? (
          <Section title="Voortgang">
            <ul className="space-y-3">
              {data.activities.map((a) => {
                const dotColor =
                  a.variant === "paid"
                    ? "bg-emerald-500"
                    : a.variant === "invoiced"
                    ? "bg-sky-500"
                    : a.variant === "credited"
                    ? "bg-rose-500"
                    : a.variant === "status"
                    ? "bg-violet-500"
                    : "bg-gray-400";
                return (
                  <li key={String(a._id)} className="flex items-start gap-3">
                    <div
                      className={`mt-1 size-2 flex-shrink-0 rounded-full ${dotColor}`}
                    />
                    <div className="flex-1 text-sm">
                      <p className="text-gray-900">{a.label}</p>
                      <p className="text-xs text-gray-500">{fmtDateTime(a.createdAt)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Section>
        ) : null}
      </div>
    </div>
  );
};

export default PartnerBtwTeruggaveDetail;
